/**
 * tm/pages/InputCheckPage.tsx — 「빠진 입력」 (2026-10-18 지도사 「오재인이 어떻게 해야 빠짐없이 입력하도록 할까 — 우리 사이트 전체 UI를 조망해서 기획 · 개발」)
 *
 *  업무관리에서 누를 것을 안 누르면 고객 · 팀장님 화면이 그 앞 상태에 멈춘다 → 빠진 입력을 한 목록에 모아 «그 줄에서 바로» 고친다.
 *  서버 규칙 = backend/services/inputCheck.js · 알림 = inputCheckJobs(오전 오재인 · 오후 지도사 일일 업무)
 *  디자인 검토(2026-10-18) 반영:
 *   - 위: 숫자 칸 두 개(꼭 고칠 것 · 확인할 것) · 규칙 칩 · 업체 찾기
 *   - 묶음은 규칙별(같은 묶음은 버튼이 같아 연달아 처리) · 묶음 안은 오래된 순 · 꼭 고칠 것 묶음에만 「고객 화면: …」 한 줄
 *   - 줄 칸 = 업체 160 | 업무 › 단계 220 | 빠진 것(가변) | 경과 56 | 고치기 320(왼쪽 맞춤 · 줄마다 같은 자리)
 *   - 버튼 두 개 안쪽은 줄 안 · 글을 쓰는 것(물어본 것 · 미승인 다음)은 줄 아래로 펼침
 *   - 확인할 것 「확인함 ▾」 = 이유(고객 사정 · 이미 처리함 · 기타 → 7일 숨김 / 해당 없음 → 계속 숨김) · 다시 나온 줄은 「다시 n」
 *   - 0건이면 한 줄 「빠진 입력이 없습니다」
 *  2026-10-25: 업무 줄 아래 단계 줄(StepTrack) — 남은 단계를 다 보이고, 누르면 그 단계까지 완료(stepFlow upto)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { TMStore } from '../hooks';
import type { Company, Consultation, Task, TaskStep } from '../types';
import * as api from '../api';
import type { InputCheck, InputCheckItem, InputRule } from '../api';
import { C, T, R } from '../components/ui';
import ConsultPeekFlow from '../components/ConsultPeekFlow';
import ConsultConfirmModal from '../components/ConsultConfirmModal';
import { LinkTaskModal } from '../components/CollectTaskPicker';   // 업무 없는 자료수집 → 업무로 잇기(2026-10-26)
import { useFlow, useFlowActions } from '../flow/FlowActions';
import { STAFF } from '../flow/flowLogic';
import type { FlowBody } from '../api';

interface Props { store: TMStore; onNavigate: (section: string, params?: unknown) => void; onCount?: (must: number) => void; me?: string }

const COLS = '160px 220px minmax(0,1fr) 56px 320px';
const btn = (extra?: CSSProperties): CSSProperties => ({
  height: 30, padding: '0 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, color: C.text,
  fontSize: T.sm, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4, ...extra,
});
const ell: CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 };

/** 업무 진행 버튼(결과 · 회신 · 미루기)을 그 줄에 — FlowActions 그대로 */
function FlowFix({ task, comp, step, kind, resultOnly, moreOnly, run, render }: {
  task: Task; comp: Company; step: TaskStep | null; kind: 'mine' | 'wait'; resultOnly?: boolean; moreOnly?: boolean;
  run: (task: Task, body: FlowBody, label?: string) => Promise<boolean>; render: (bar: ReactNode, panel: ReactNode) => ReactNode;
}) {
  const { bar, panel } = useFlowActions({ task, comp, step, kind, run, compact: true, panelIndent: 14, resultOnly, moreOnly });
  return <>{render(bar, panel)}</>;
}

/** 단계 줄(2026-10-25 지도사 「다음 단계들도 여기서 다 보고, 어디까지 했는지 체크하면 그 앞까지 완료」)
 *  업무 칸 아래 한 줄 — 끝난 앞 단계는 「✓ 1~n 완료」 하나로 · 남은 단계는 칩 · 칩에 올리면 그 앞까지 함께 표시 · 누르면 여기까지 완료(upto · 되돌리기 알림 없음) */
function StepTrack({ task, onUpto }: { task: Task; onUpto: (s: TaskStep) => Promise<void> }) {
  const [hover, setHover] = useState(-1);
  const [busy, setBusy] = useState(false);
  const steps = (task.steps || []).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const first = steps.findIndex(s => s.status !== '완료');
  if (first < 0) return null;
  // 앞에서부터 이어진 완료 단계만 접는다(가운데 완료는 칩으로 남긴다)
  const doneHead = first;
  const chip = (on: boolean, cur: boolean, done: boolean): CSSProperties => ({
    height: 26, maxWidth: 176, padding: '0 9px 0 7px', borderRadius: R.sm, display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: T.xs, fontWeight: cur ? 700 : 500,
    border: `1px solid ${on ? C.green : cur ? C.accent : C.border}`, background: on ? C.greenLt : done ? C.bgAlt : C.white,
    color: on ? '#15803D' : done ? C.muted : cur ? C.accent : C.sub, cursor: done || busy ? 'default' : 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
  });
  const box = (on: boolean, done: boolean): CSSProperties => ({
    width: 12, height: 12, borderRadius: 3, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, lineHeight: 1,
    border: `1.5px solid ${on || done ? (done ? C.muted : C.green) : C.borderDk}`, background: on ? C.green : 'transparent', color: '#fff',
  });
  const go = async (s: TaskStep) => { if (busy) return; setBusy(true); try { await onUpto(s); } finally { setBusy(false); setHover(-1); } };
  return (
    <div className="ic-steps" onMouseLeave={() => setHover(-1)} style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', padding: '0 14px 9px 186px', opacity: busy ? 0.6 : 1 }}>
      {doneHead > 0 && (
        <span title={steps.slice(0, doneHead).map((s, i) => `${i + 1}. ${s.name}`).join('\n')} style={chip(false, false, true)}>
          <span style={{ color: C.green, fontWeight: 700 }}>✓</span><span style={{ fontVariantNumeric: 'tabular-nums' }}>{doneHead === 1 ? '1 완료' : `1~${doneHead} 완료`}</span>
        </span>
      )}
      {steps.slice(doneHead).map((s, k) => {
        const i = doneHead + k, done = s.status === '완료', cur = i === first;
        const on = !done && hover >= i;
        return (
          <button key={s.id} type="button" disabled={done || busy} aria-label={done ? `${i + 1}. ${s.name} 완료` : `${i + 1}. ${s.name}까지 완료로`}
            title={done ? `${s.name} (완료)` : `${s.name}\n누르면 여기까지 완료`}
            onMouseEnter={() => setHover(done ? -1 : i)} onFocus={() => setHover(done ? -1 : i)} onBlur={() => setHover(-1)}
            onClick={() => void go(s)} style={chip(on, cur, done)}>
            <span style={box(on, done)}>{on || done ? '✓' : ''}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums', color: on ? '#15803D' : C.muted }}>{i + 1}</span>
            <span style={ell}>{s.name}</span>
            {cur && s.status === '회신대기' ? <span style={{ color: C.purple, fontWeight: 700 }}>회신 대기</span> : null}
          </button>
        );
      })}
    </div>
  );
}

export default function InputCheckPage({ store, onNavigate, onCount, me }: Props) {
  const [data, setData] = useState<InputCheck | null>(null);
  const [err, setErr] = useState('');
  const [rule, setRule] = useState<InputRule | 'all'>('all');
  // 담당(2026-10-18) — 실무 담당 계정은 처음에 내 것(+ 공통 상담)만 · 「전체」로 바꿀 수 있다
  const [who, setWho] = useState<string>(() => (me && STAFF.includes(me) ? me : 'all'));
  const ofWho = (x: InputCheckItem, w: string) => w === 'all' || !x.who || x.who === w;
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);        // 펼친 줄(물어본 것 · 확인함)
  const [linkFor, setLinkFor] = useState<{ id: string; name: string; project: string; bizNo?: string; ceo?: string } | null>(null);
  const [text, setText] = useState('');
  const [date, setDate] = useState('');
  const [toast, setToast] = useState<{ text: string; undo?: () => Promise<void> } | null>(null);
  const [modal, setModal] = useState<{ mode: 'peek' | 'confirm'; c: Consultation } | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.getInputCheck();
      if (!r.success || !r.data) throw new Error(r.error || '불러오지 못했어요');
      setData(r.data); setErr('');
      if (onCount) onCount(me && STAFF.includes(me) ? r.data.items.filter(x => x.level === 'must' && (!x.who || x.who === me)).length : r.data.counts.must);   // 메뉴 숫자 = 내 몫(실무 담당) · 그 밖은 전체
    } catch (e) { setErr(e instanceof Error ? e.message : '불러오지 못했어요'); }
  }, [onCount, me]);
  useEffect(() => { void load(); }, [load]);
  const say = (t: string, undo?: () => Promise<void>) => { setToast({ text: t, undo }); window.setTimeout(() => setToast(cur => (cur && cur.text === t ? null : cur)), 10000); };
  const changed = useCallback(async () => { await store.refreshTasks(); await load(); }, [store, load]);
  const { run, undoNode } = useFlow(changed, m => say(m));

  const items = useMemo(() => {
    const k = q.trim().toLowerCase();
    return (data ? data.items : []).filter(x => ofWho(x, who) && (rule === 'all' || x.rule === rule) && (!k || `${x.company} ${x.task} ${x.step || ''}`.toLowerCase().includes(k)));
  }, [data, rule, q, who]);   // eslint-disable-line react-hooks/exhaustive-deps
  const groups = useMemo(() => {
    const m = new Map<InputRule, InputCheckItem[]>();
    items.forEach(x => { if (!m.has(x.rule)) m.set(x.rule, []); (m.get(x.rule) as InputCheckItem[]).push(x); });
    return Array.from(m.entries());
  }, [items]);

  const taskOf = (id?: string | null) => (id ? store.tasks.find(t => t.id === id) : undefined);
  const compOf = (id?: string | null) => (id ? store.companies.find(c => c.id === id) : undefined);
  const stepOf = (t: Task | undefined, id?: string) => (t && id ? (t.steps || []).find(s => s.id === id) : undefined);

  /** 「확인함」으로 넘기면 며칠 뒤 다시 — 자료 재요청은 3일(재요청 주기) · 나머지 7일 (서버 tm-input 과 같은 값) */
  const laterDays = (it: InputCheckItem) => (it.rule === 'redo' ? 3 : 7);
  async function mark(it: InputCheckItem, kind: 'later' | 'never', reason?: string) {
    const r = await api.markInputCheck(it.key, kind, reason);
    if (!r.success) { say(r.error || '저장하지 못했어요'); return; }
    setOpen(null);
    await load();
    say(kind === 'never' ? `${it.company} · ${it.label} — 해당 없음으로 뺐어요` : `${it.company} · ${it.label} — ${laterDays(it)}일 뒤 다시 보여요`, async () => { await api.unmarkInputCheck(it.key); await load(); });
  }
  async function saveDue(it: InputCheckItem) {
    if (!it.task_id || !it.step_id || !date) return;
    try { await api.updateStep(it.task_id, it.step_id, { due: date } as Partial<TaskStep>); setDate(''); setOpen(null); await changed(); say(`${it.company} · 「${it.step}」 기한을 ${+date.slice(5, 7)}/${+date.slice(8, 10)}로 적었어요`); }
    catch { say('기한을 저장하지 못했어요'); }
  }
  async function saveAsk(it: InputCheckItem) {
    const t = taskOf(it.task_id);
    if (!t || !it.step_id || !text.trim()) return;
    if (await run(t, { action: 'ask', stepId: it.step_id, question: text.trim() }, it.company)) { setText(''); setOpen(null); }
  }
  /** 여기까지 완료 — 되돌리기 알림 없이 바로(2026-10-25 지도사 「되돌리기 표시는 따로 하지마」) · 실패만 알림 */
  async function upto(t: Task, s: TaskStep) {
    try {
      const r = await api.taskFlow(t.id, { action: 'upto', stepId: s.id });
      if (!r.success) { say(r.error || '바꾸지 못했어요'); return; }
      await changed();
    } catch { say('바꾸지 못했어요'); }
  }
  function openConsult(it: InputCheckItem, mode: 'peek' | 'confirm') {
    const c = store.consultations.find(x => x.id === it.consult_id);
    if (c) setModal({ mode, c }); else onNavigate('consultation');
  }

  /** 줄 하나 — 고치기 칸 · 줄 아래 펼침 */
  function row(it: InputCheckItem) {
    const t = taskOf(it.task_id), comp = compOf(it.company_id || (t ? t.company_id : null)), st = stepOf(t, it.step_id);
    const isOpen = open === it.key;
    const openTask = () => onNavigate('companies', { companyId: it.company_id || (t ? t.company_id : undefined), taskId: it.task_id || undefined });
    const later = it.level === 'should'
      ? <button onClick={() => { setOpen(isOpen ? null : it.key); setText(''); }} style={btn({ color: C.sub, borderColor: isOpen ? C.accent : C.border })}>확인함 ▾</button>
      : <button onClick={() => void mark(it, 'never', '해당 없음')} title="이 줄은 맞지 않아요 — 다시 보이지 않게" style={btn({ border: 'none', background: 'none', color: C.muted, fontWeight: 500, padding: '0 4px' })}>해당 없음</button>;
    let fix: ReactNode = null, below: ReactNode = null;
    const track = t && (t.steps || []).length ? <StepTrack task={t} onUpto={s => upto(t, s)} /> : null;
    const cells = (fixNode: ReactNode, belowNode: ReactNode, hasMore?: boolean, moreNode?: ReactNode, morePanel?: ReactNode) => (
      <div key={it.key} style={{ borderBottom: `1px solid ${C.border}` }}>
        <div className="ic-row" style={{ display: 'grid', gridTemplateColumns: COLS, alignItems: 'center', gap: 12, padding: '9px 14px' }}>
          <span title={it.company} style={{ ...ell, fontSize: T.sm, fontWeight: 700, color: C.text }}>{it.company || '업체 없음'}</span>
          <span title={`${it.task}${it.step ? ' › ' + it.step : ''}`} style={{ ...ell, fontSize: T.sm, color: C.sub }}>{it.task}{it.step ? <> <span style={{ color: C.muted }}>›</span> {it.step}</> : null}</span>
          <span style={{ ...ell, fontSize: T.sm, fontWeight: 700, color: it.level === 'must' ? '#B91C1C' : C.text }}>
            {it.title}{it.extra && it.extra.n ? ` (${it.extra.n}개)` : ''}
            {it.again > 0 && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 4, background: '#FFF7ED', color: '#C2410C', fontSize: T.xs, fontWeight: 700 }}>다시 {it.again}</span>}
          </span>
          <span style={{ fontSize: T.sm, color: it.days >= 7 ? '#B91C1C' : C.sub, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: it.days >= 7 ? 700 : 500 }}>{it.days ? `${it.days}일째` : '오늘'}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', minWidth: 0 }}>{fixNode}{later}{hasMore ? null : moreNode}</div>
        </div>
        {track}
        {belowNode}
        {morePanel}
        {isOpen && it.level === 'should' && (
          <div style={{ margin: '0 14px 10px', padding: '8px 10px', borderRadius: R.md, background: C.bgAlt, border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: T.sm, fontWeight: 700, color: C.text, marginRight: 4 }}>왜 넘기나요?</span>
            {['고객 사정', '이미 처리함', '기타'].map(r => <button key={r} onClick={() => void mark(it, 'later', r)} style={btn()}>{r}</button>)}
            <span style={{ fontSize: T.xs, color: C.muted }}>{laterDays(it)}일 뒤 다시 보여요</span>
            <button onClick={() => void mark(it, 'never', '해당 없음')} style={btn({ marginLeft: 'auto', color: C.sub })}>해당 없음(계속 빼기)</button>
          </div>
        )}
      </div>
    );
    // 업무 진행 버튼이 필요한 줄
    // 자료 재요청(redo)도 회신 대기와 같은 버튼(회신 받음 · 재촉 문자…) — 재촉하면 그날부터 3일 다시 셈
    const flowKind = it.rule === 'wait' || it.rule === 'redo' ? 'wait' : it.rule === 'result' || it.rule === 'resdue' || it.rule === 'stale' ? 'mine' : null;
    if (flowKind && t && comp && st) {
      return <FlowFix key={it.key} task={t} comp={comp} step={st} kind={flowKind} resultOnly={it.rule === 'result'} run={run} render={(bar, panel) => cells(bar, panel, true)} />;
    }
    switch (it.rule) {
      case 'noq':
        fix = <button onClick={() => { setOpen(isOpen ? null : it.key); setText(''); }} style={btn({ color: C.accent, borderColor: C.accent })}>물어본 것 적기 ▾</button>;
        if (isOpen) below = (
          <div style={{ margin: '0 14px 10px', display: 'flex', gap: 6 }}>
            <input autoFocus value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') void saveAsk(it); }} maxLength={200}
              placeholder={`${(it.extra && it.extra.who ? it.extra.who : ['업체', 'FP']).join('·')}에게 물어본 것 — 예) 최근 3년 재무제표 보내 주세요`}
              style={{ flex: 1, minWidth: 0, height: 32, padding: '0 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none' }} />
            <button disabled={!text.trim()} onClick={() => void saveAsk(it)} style={btn({ background: C.accent, borderColor: C.accent, color: '#fff', opacity: text.trim() ? 1 : 0.5 })}>저장</button>
          </div>
        );
        break;
      case 'due':
        fix = (
          <span style={{ display: 'inline-flex', gap: 4 }}>
            <input type="date" value={open === it.key ? date : ''} onFocus={() => { if (open !== it.key) { setOpen(it.key); setDate(''); } }} onChange={e => { setOpen(it.key); setDate(e.target.value); }}
              style={{ height: 30, padding: '0 6px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm }} />
            <button disabled={!(open === it.key && date)} onClick={() => void saveDue(it)} style={btn({ background: C.accent, borderColor: C.accent, color: '#fff', opacity: open === it.key && date ? 1 : 0.5 })}>저장</button>
          </span>
        );
        break;
      case 'consult': fix = <button onClick={() => openConsult(it, 'peek')} style={btn({ color: C.accent, borderColor: C.accent })}>결과 입력</button>; break;
      case 'tent': fix = <button onClick={() => openConsult(it, 'confirm')} style={btn({ color: C.accent, borderColor: C.accent })}>일정 확정</button>; break;
      case 'loan': fix = <button onClick={() => onNavigate('loans')} style={btn()}>대출 점검 열기</button>; break;
      case 'upload': fix = <button onClick={openTask} style={btn()}>자료 확인</button>; break;
      case 'notask': fix = <button onClick={() => { const x = it.extra || {}; if (x.project_id) setLinkFor({ id: x.project_id, name: x.name || it.company, project: x.project || it.task, bizNo: x.biz_no, ceo: x.ceo }); }} style={btn({ color: C.accent, borderColor: C.accent })}>업무로 잇기</button>; break;
      default: fix = <button onClick={openTask} style={btn()}>업무 열기</button>;
    }
    // 진행 버튼이 없는 줄에도 같은 ⋯ (업무 전체 완료 · 보류 · 중단 · 지우기) — 2026-10-27 지도사 「여기서도 업무 종료, 삭제 해 버릴 수 있게」 · 대시보드와 같은 메뉴
    if (t && comp) return <FlowFix key={it.key} task={t} comp={comp} step={st || null} kind="mine" moreOnly run={run} render={(bar, panel) => cells(fix, below, false, bar, panel)} />;
    return cells(fix, below);
  }

  const rules = data ? data.rules : null;
  const mineAll = data ? data.items.filter(x => ofWho(x, who)) : [];   // 고른 담당의 줄(규칙 칩 · 숫자 칸은 이것으로)
  const nRule = (k: InputRule) => mineAll.filter(x => x.rule === k).length;
  const chips: [InputRule | 'all', string, number][] = data
    ? [['all', '전체', mineAll.length] as [InputRule | 'all', string, number]].concat((Object.keys(data.rules) as InputRule[]).filter(k => nRule(k) > 0).map(k => [k, data.rules[k].label, nRule(k)]))
    : [];
  const stat = (label: string, n: number, note: string, color: string) => (
    <div style={{ flex: 1, minWidth: 200, padding: '12px 16px', borderRadius: R.md, background: C.white, border: `1px solid ${C.border}` }}>
      <div style={{ fontSize: T.xs, fontWeight: 700, color: C.sub }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 2 }}>
        <span style={{ fontSize: 26, fontWeight: 800, color: n ? color : C.muted, fontVariantNumeric: 'tabular-nums' }}>{n}</span>
        <span style={{ fontSize: T.xs, color: C.muted }}>{note}</span>
      </div>
    </div>
  );

  return (
    <div style={{ padding: 16, overflowY: 'auto', height: '100%', boxSizing: 'border-box' }}>
      {linkFor && <LinkTaskModal project={linkFor} onClose={() => setLinkFor(null)} onDone={(m) => { setLinkFor(null); say(m); void changed(); }} />}
      <style>{`@media (max-width: 1100px){ .ic-row{ grid-template-columns: 1fr 1fr !important; } .ic-steps{ padding-left: 14px !important; } }`}</style>
      {modal && modal.mode === 'peek' && (
        <ConsultPeekFlow consultation={modal.c} fps={store.fps} wms={store.wms} templates={store.taskTemplates} taskCategories={store.taskCategories}
          aiPresets={store.aiPresets} onDone={async () => { setModal(null); await store.refresh(); await load(); }} onCancel={() => setModal(null)} onToast={m => say(m)} onNavigate={onNavigate} />
      )}
      {modal && modal.mode === 'confirm' && <ConsultConfirmModal consultation={modal.c} onDone={async () => { setModal(null); await store.refresh(); await load(); }} onCancel={() => setModal(null)} onToast={m => say(m)} />}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        {stat('꼭 고칠 것', mineAll.filter(x => x.level === 'must').length, '고객 · 팀장님 화면이 틀리게 보여요', '#B91C1C')}
        {stat('확인할 것', mineAll.filter(x => x.level === 'should').length, '오래됐거나 잊기 쉬운 것', C.text)}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        {chips.map(([k, label, n]) => (
          <button key={k} onClick={() => setRule(k)} aria-pressed={rule === k}
            style={btn({ borderRadius: 15, background: rule === k ? C.primary : C.white, color: rule === k ? '#fff' : C.sub, borderColor: rule === k ? C.primary : C.border, fontVariantNumeric: 'tabular-nums' })}>{label} {n}</button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: T.xs, fontWeight: 700, color: C.sub }}>담당</span>
        <select value={who} onChange={e => setWho(e.target.value)} style={{ height: 30, padding: '0 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, background: C.white }}>
          <option value="all">전체</option>
          {STAFF.map(s => <option key={s} value={s}>{s} {data ? data.items.filter(x => ofWho(x, s)).length : ''}</option>)}
        </select>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="업체 · 업무 찾기"
          style={{ width: 200, height: 30, padding: '0 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none' }} />
      </div>

      {data && items.length > 0 && <div style={{ margin: '-4px 0 10px', fontSize: T.xs, color: C.muted }}>업무 아래 단계를 누르면 그 단계까지 완료돼요. 넘어간 결과 단계는 통과로 남아요.</div>}
      {err && <div style={{ padding: 14, borderRadius: R.md, background: '#FEF2F2', color: '#B91C1C', fontSize: T.sm }}>{err} <button onClick={() => void load()} style={btn({ marginLeft: 8 })}>다시</button></div>}
      {!err && !data && <div style={{ padding: 14, color: C.muted, fontSize: T.sm }}>불러오는 중</div>}
      {data && items.length === 0 && <div style={{ padding: '18px 16px', borderRadius: R.md, background: C.white, border: `1px solid ${C.border}`, fontSize: T.md, color: C.sub }}>빠진 입력이 없습니다{data.counts.hidden ? <span style={{ fontSize: T.xs, color: C.muted, marginLeft: 8 }}>넘겨 둔 줄 {data.counts.hidden}개</span> : null}</div>}

      {groups.map(([k, list]) => (
        <section key={k} style={{ marginBottom: 14, borderRadius: R.md, background: C.white, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
          <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'baseline', gap: 10, background: '#F8FAFC', borderBottom: `1px solid ${C.border}` }}>
            <b style={{ fontSize: T.md, color: rules && rules[k].level === 'must' ? '#B91C1C' : C.text }}>{rules ? rules[k].title : k}</b>
            <span style={{ fontSize: T.sm, color: C.sub, fontVariantNumeric: 'tabular-nums' }}>{list.length}건</span>
            {rules && rules[k].shows && <span style={{ fontSize: T.xs, color: C.muted, ...ell }}>{rules[k].shows}</span>}
          </div>
          {list.map(row)}
        </section>
      ))}

      {undoNode}
      {toast && (
        <div role="status" style={{ position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)', zIndex: 3000, display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderRadius: 10, background: '#111827', color: '#fff', fontSize: T.sm, boxShadow: '0 8px 24px rgba(0,0,0,.2)', maxWidth: 'calc(100vw - 32px)' }}>
          <span style={ell}>{toast.text}</span>
          {toast.undo && <button onClick={() => { const u = toast.undo; setToast(null); if (u) void u(); }} style={{ border: 'none', background: 'none', color: '#93C5FD', fontWeight: 700, cursor: 'pointer', fontSize: T.sm }}>되돌리기</button>}
        </div>
      )}
    </div>
  );
}
